import { formatMoney, parseDecimal } from "@bw/shared/utils";
import type { UnifiedEnrollmentResponse } from "@bw/shared/types/generated";
import {
  Box,
  Button,
  Chip,
  Link,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { ClaimSection } from "./ClaimSection";
import { useLearningSpace } from "../hooks/useLearningSpace";

/**
 * One row in the player's enrollment list. Renders the status + fee paid from the
 * response verbatim (no derivation). Actions depend on the server status:
 *   - enrolled / waitlisted → Cancel (with an inline confirm step).
 *   - promoted (a waitlist spot opened) → Claim (pays if the track has a fee) +
 *     Decline. The claim flow lives in <ClaimSection>: it attempts a naked claim
 *     and, on a "card required" backend rejection, reveals the facility-scoped
 *     payment spine so a PAID promoted spot can actually be paid for and claimed.
 *   - terminal states (cancelled / declined) → no actions.
 *
 * The status label maps the raw backend status to readable copy but always
 * reflects the server's value — it never invents a state the response didn't
 * carry.
 */

const STATUS_LABEL: Record<string, string> = {
  enrolled: "Enrolled",
  waitlisted: "Waitlisted",
  promoted: "Spot available",
  payment_pending: "Payment pending",
  cancelled: "Cancelled",
  declined: "Declined",
};

function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

export function EnrollmentRow({
  enrollment,
  canAct,
  confirmingCancel,
  onRequestCancel,
  onConfirmCancel,
  busy,
}: {
  enrollment: UnifiedEnrollmentResponse;
  canAct: () => boolean;
  confirmingCancel: boolean;
  onRequestCancel: () => void;
  onConfirmCancel: () => void;
  busy: boolean;
}) {
  const { status } = enrollment;
  const isPromoted = status === "promoted";
  const isActive = status === "enrolled" || status === "waitlisted";
  const programName = enrollment.program_name?.trim();
  const trackName = enrollment.track_name?.trim();
  const learningSpace = useLearningSpace(
    enrollment.program_id,
    status === "enrolled" && (enrollment.enrollment_mode === "full_course" || enrollment.enrollment_mode === "remaining_course"),
  );

  // Show a money figure ONLY when a real charge occurred. fee_paid is
  // Numeric(10,2) NOT NULL defaulting to "0.00" for waitlisted /
  // promoted-unclaimed / cancelled / declined rows where no money changed
  // hands — a "$0.00" there exposes no state and misreads as a broken price.
  const paid = parseDecimal(enrollment.fee_paid) ?? 0;
  const hasPaid = paid > 0;

  return (
    <ListItem
      component="li"
      divider
      data-status={status}
      sx={{ px: 2, py: 1.5 }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{
          width: "100%",
          justifyContent: "space-between",
          alignItems: { sm: "center" },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h2"
            variant="subtitle1"
            sx={{ fontWeight: 600, overflowWrap: "anywhere" }}
          >
            {programName && enrollment.facility_id ? (
              <Link
                component={RouterLink}
                color="inherit"
                underline="hover"
                to={`/classes/${enrollment.facility_id}/${enrollment.program_id}`}
              >
                {programName}
              </Link>
            ) : (
              programName || "Class unavailable"
            )}
          </Typography>
          {trackName ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.75, overflowWrap: "anywhere" }}
            >
              {trackName}
            </Typography>
          ) : null}
          <Chip size="small" variant="outlined" label={statusLabel(status)} />
          {hasPaid ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75 }}
            >
              {status === "payment_pending" ? "Amount awaiting confirmation: " : ""}
              {formatMoney(enrollment.fee_paid, enrollment.currency)}
            </Typography>
          ) : null}
        </Box>

        <ClaimSection enrollment={enrollment} canAct={canAct} />
        {!isPromoted ? (
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            {learningSpace.data ? (
              <Button
                component={RouterLink}
                size="small"
                variant="outlined"
                to={`/community/communities/${learningSpace.data.community_id}`}
              >
                Learning Space
              </Button>
            ) : null}
            {learningSpace.data && enrollment.facility_id ? (
              <Button component={RouterLink} size="small" variant="outlined"
                to={`/classes/${enrollment.facility_id}/${enrollment.program_id}/learning/${enrollment.enrollment_id}`}>
                Feedback and attendance
              </Button>
            ) : null}
            {status === "enrolled" ? <Button component={RouterLink} size="small" variant="outlined" to={`/classes/${enrollment.program_id}/rate-coach`}>Rate coach</Button> : null}
            {status === "enrolled" && enrollment.transaction_id ? (
              <Button component={RouterLink} size="small" variant="outlined"
                to={`/me/transactions/${encodeURIComponent(enrollment.transaction_id)}`}>
                View receipt
              </Button>
            ) : null}
            {isActive ? (
              confirmingCancel ? (
                <Button
                  color="error"
                  variant="contained"
                  size="small"
                  onClick={onConfirmCancel}
                  disabled={busy}
                >
                  Confirm cancel
                </Button>
              ) : (
                <Button
                  color="error"
                  variant="outlined"
                  size="small"
                  onClick={onRequestCancel}
                  disabled={busy}
                >
                  Cancel
                </Button>
              )
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </ListItem>
  );
}
